import { useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { LogoMark } from '../components/Logo';
import { Button } from '../components/ui/button';
import { ShieldAlert, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const EmergencyLockPage = () => {
  const [token, setToken] = useState('');
  const [state, setState] = useState('confirm'); // confirm | working | locked | invalid
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const value = new URLSearchParams(window.location.search).get('token') || '';
    if (!value) {
      setState('invalid');
      return;
    }
    setToken(value);
  }, []);

  const handleLock = async () => {
    setState('working');
    try {
      await authAPI.emergencyLock(token);
      // Remove the single-use token from the address bar now that it is consumed
      window.history.replaceState({}, document.title, '/security/emergency-lock');
      setState('locked');
      setToken('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'This lock link is invalid or has expired.');
      setState('invalid');
      setToken('');
    }
  };

  return (
    <div className="min-h-screen bg-sand flex flex-col items-center justify-center px-4 py-12" data-testid="emergency-lock-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <LogoMark className="h-12 w-12" />
        </div>

        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          {state === 'confirm' && (
            <div className="p-8 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-5">
                <ShieldAlert className="w-7 h-7 text-red-600" />
              </div>
              <h1 className="font-heading font-semibold text-2xl mb-3 text-foreground">Lock this account?</h1>
              <p className="text-sm text-muted-foreground mb-6">
                This will immediately sign out every active session, revoke all trusted devices,
                and block all future logins until an administrator restores access.
              </p>
              <Button
                onClick={handleLock}
                className="w-full h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold"
                data-testid="emergency-lock-confirm-btn"
              >
                This wasn't me — lock my account
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                This secure link can be used once and expires 24 hours after it was sent.
              </p>
            </div>
          )}

          {state === 'working' && (
            <div className="p-10 text-center">
              <Loader2 className="w-8 h-8 text-forest animate-spin mx-auto mb-4" />
              <p className="text-sm text-muted-foreground" data-testid="emergency-lock-working">Locking your account…</p>
            </div>
          )}

          {state === 'locked' && (
            <div className="p-8 text-center" data-testid="emergency-lock-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <h1 className="font-heading font-semibold text-2xl mb-3 text-foreground">Account locked</h1>
              <p className="text-sm text-muted-foreground mb-2">
                All sessions and trusted devices have been revoked. No one can sign in to this account.
              </p>
              <p className="text-sm text-muted-foreground">
                Contact support to restore access:{' '}
                <a href="mailto:admin@campia.co.za" className="text-forest font-semibold">admin@campia.co.za</a>
              </p>
            </div>
          )}

          {state === 'invalid' && (
            <div className="p-8 text-center" data-testid="emergency-lock-invalid">
              <div className="mx-auto w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-5">
                <XCircle className="w-7 h-7 text-muted-foreground" />
              </div>
              <h1 className="font-heading font-semibold text-2xl mb-3 text-foreground">Link invalid or expired</h1>
              <p className="text-sm text-muted-foreground">
                {errorMsg || 'This lock link is no longer valid. If you still need to secure the account, contact support.'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <a href="mailto:admin@campia.co.za" className="text-forest font-semibold">admin@campia.co.za</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmergencyLockPage;
